import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const HydrationReminder = () => {
  const checkIntervalRef = useRef(null);
  const notificationPermissionRef = useRef(false);

  useEffect(() => {
    // Guard for SSR - window is not available during server-side rendering
    if (typeof window === 'undefined') return;
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        notificationPermissionRef.current = permission === 'granted';
      });
    } else if ('Notification' in window && Notification.permission === 'granted') {
      notificationPermissionRef.current = true;
    }

    // Start checking every 30 minutes
    checkIntervalRef.current = setInterval(() => {
      checkHydrationReminder();
    }, 30 * 60 * 1000); // Check every 30 minutes

    // Also check immediately on mount
    checkHydrationReminder();

    // Cleanup on unmount
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  const checkHydrationReminder = async () => {
    try {
      // Check if current time is between 9 AM and 9 PM
      const now = new Date();
      const currentHour = now.getHours();
      
      if (currentHour < 9 || currentHour >= 21) {
        // Outside reminder hours
        return;
      }

      // Get last water intake time
      const response = await axios.get(`${API}/api/water-intake/last-entry`);
      const lastIntake = response.data.last_entry;

      if (!lastIntake) {
        // No water logged today, send reminder
        sendHydrationNotification();
        return;
      }

      // Check if last intake was more than 4 hours ago
      const lastIntakeTime = new Date(lastIntake.recorded_at);
      const hoursSinceLastIntake = (now - lastIntakeTime) / (1000 * 60 * 60);

      if (hoursSinceLastIntake >= 4) {
        sendHydrationNotification();
      }
    } catch (error) {
      console.error('Failed to check hydration reminder:', error);
    }
  };

  const sendHydrationNotification = () => {
    // Show browser notification
    if (typeof window !== 'undefined' && notificationPermissionRef.current && 'Notification' in window) {
      new Notification('💧 Hydration Reminder', {
        body: 'Take a minute to fuel your body with some quality H2O! Stay hydrated for peak performance.',
        icon: '/favicon.ico',
        tag: 'hydration-reminder',
        requireInteraction: false
      });
    }

    // Also show toast notification
    toast.info('💧 Hydration Reminder: Time to drink some water!', {
      duration: 5000,
      description: 'Stay hydrated for peak athletic performance'
    });
  };

  // This component doesn't render anything
  return null;
};

export default HydrationReminder;
